import { Camera, CameraOff, Check, RefreshCw, ShieldCheck, UserRound, X } from "lucide-react";
import { ChangeEvent, useEffect, useRef, useState } from "react";

const AVATAR_SIZE = 160;
const MAX_SOURCE_BYTES = 8 * 1024 * 1024;
const MAX_DATA_URL_LENGTH = 120_000;

interface Props {
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
}

type CaptureStage = "closed" | "camera" | "review" | "error";

export function AvatarCapture({ name, value, onChange }: Props) {
  const [stage, setStage] = useState<CaptureStage>("closed");
  const [requesting, setRequesting] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRequestRef = useRef(0);

  const stopCamera = () => {
    cameraRequestRef.current += 1;
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(
    () => () => {
      stopCamera();
    },
    []
  );

  useEffect(() => {
    if (stage === "closed") return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        stopCamera();
        setStage("closed");
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [stage]);

  const startCamera = async () => {
    stopCamera();
    const requestId = ++cameraRequestRef.current;
    setCameraError("");
    setRequesting(true);
    setStage("camera");

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Live camera capture is not available in this browser.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 720 }
        }
      });
      if (requestId !== cameraRequestRef.current) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      streamRef.current = stream;
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (requestId !== cameraRequestRef.current) return;
      if (!videoRef.current) throw new Error("The camera preview could not be opened.");
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    } catch (error) {
      if (requestId !== cameraRequestRef.current) return;
      stopCamera();
      setRequesting(false);
      setCameraError(
        error instanceof Error
          ? error.message
          : "The camera could not be opened. You can choose a photo or skip."
      );
      setStage("error");
    } finally {
      if (requestId === cameraRequestRef.current) setRequesting(false);
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError("The camera is still warming up. Try again in a moment.");
      return;
    }
    onChange(makeSquareAvatar(video, video.videoWidth, video.videoHeight));
    setSkipped(false);
    stopCamera();
    setStage("review");
  };

  const choosePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > MAX_SOURCE_BYTES) {
      setCameraError("Choose an image smaller than 8 MB.");
      setStage("error");
      return;
    }

    try {
      const bitmap = await createImageBitmap(file);
      onChange(makeSquareAvatar(bitmap, bitmap.width, bitmap.height));
      bitmap.close();
      setSkipped(false);
      stopCamera();
      setCameraError("");
      setStage("review");
    } catch {
      setCameraError("That image could not be read. Try another or skip the photo.");
      setStage("error");
    }
  };

  const skipPhoto = () => {
    stopCamera();
    onChange(null);
    setSkipped(true);
    setCameraError("");
    setStage("closed");
  };

  const initial = name.trim().slice(0, 1).toUpperCase() || "?";

  return (
    <>
      <section className="avatar-choice" aria-label="Optional room avatar">
        <AvatarPreview value={value} initial={initial} />
        <span className="avatar-choice__copy">
          <b>{value ? "Face card ready" : skipped ? "Photo skipped" : "Your face card"}</b>
          <small>{value ? "Only this room receives the small copy" : "Optional · your initial is the fallback"}</small>
        </span>
        <span className="avatar-choice__actions">
          <button type="button" onClick={startCamera}>
            <Camera size={15} />
            {value ? "Retake" : "Add photo"}
          </button>
          <button type="button" onClick={skipPhoto}>
            Skip
          </button>
        </span>
      </section>

      {stage !== "closed" && (
        <div className="avatar-capture-backdrop" role="presentation">
          <section
            className="avatar-capture"
            role="dialog"
            aria-modal="true"
            aria-labelledby="avatar-capture-title"
          >
            <header>
              <span>
                <ShieldCheck size={19} />
                <b id="avatar-capture-title">Make your face card</b>
              </span>
              <button
                className="icon-button icon-button--small"
                type="button"
                aria-label="Close camera"
                onClick={() => {
                  stopCamera();
                  setStage("closed");
                }}
              >
                <X size={16} />
              </button>
            </header>

            <div className="avatar-capture__viewport">
              {stage === "camera" && (
                <>
                  <video ref={videoRef} autoPlay muted playsInline />
                  {requesting && (
                    <span className="avatar-capture__status">
                      <Camera size={20} />
                      Waiting for permission…
                    </span>
                  )}
                </>
              )}
              {stage === "review" && value && <img src={value} alt="Your captured face" />}
              {stage === "error" && (
                <span className="avatar-capture__status">
                  <CameraOff size={24} />
                  Camera unavailable
                </span>
              )}
            </div>

            {cameraError && <p className="avatar-capture__error">{cameraError}</p>}
            <p className="avatar-capture__privacy">
              Permission is requested only now. VEIL crops a small square and sends no original file.
            </p>

            <div className="avatar-capture__actions">
              {stage === "camera" && (
                <button className="button button--primary" type="button" disabled={requesting} onClick={takePhoto}>
                  <Camera size={17} /> Take photo
                </button>
              )}
              {stage === "review" && (
                <>
                  <button className="button button--ghost" type="button" onClick={startCamera}>
                    <RefreshCw size={16} /> Retake
                  </button>
                  <button className="button button--primary" type="button" onClick={() => setStage("closed")}>
                    <Check size={17} /> Use this photo
                  </button>
                </>
              )}
              {stage === "error" && (
                <label className="button button--ghost avatar-file-button">
                  <UserRound size={16} /> Choose a photo
                  <input type="file" accept="image/*" capture="user" onChange={choosePhoto} />
                </label>
              )}
              <button className="avatar-capture__skip" type="button" onClick={skipPhoto}>
                Skip photo
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function AvatarPreview({ value, initial }: { value: string | null; initial: string }) {
  return (
    <span className="avatar-preview" aria-hidden="true">
      {value ? <img src={value} alt="" /> : initial}
    </span>
  );
}

function makeSquareAvatar(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number
) {
  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image processing is unavailable.");

  const edge = Math.min(sourceWidth, sourceHeight);
  const sourceX = (sourceWidth - edge) / 2;
  const sourceY = (sourceHeight - edge) / 2;
  context.drawImage(
    source,
    sourceX,
    sourceY,
    edge,
    edge,
    0,
    0,
    AVATAR_SIZE,
    AVATAR_SIZE
  );

  const candidates = [
    canvas.toDataURL("image/webp", 0.78),
    canvas.toDataURL("image/jpeg", 0.72),
    canvas.toDataURL("image/jpeg", 0.5)
  ];
  return candidates.find((candidate) => candidate.length <= MAX_DATA_URL_LENGTH) ?? candidates.at(-1)!;
}
